
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { 
  Activity, 
  CheckCircle, 
  AlertTriangle, 
  XCircle,
  Server,
  Database,
  Users,
  Clock,
  RefreshCw
} from "lucide-react";
import { useRealTimeStats } from "@/hooks/useRealTimeStats";
import { useEnhancedSystemHealth } from "@/hooks/useEnhancedSystemHealth";
import { Button } from "@/components/ui/button";

export const SystemHealthPanel = () => {
  const { data: stats, isLoading, error } = useRealTimeStats();
  const { health, checkSystemHealth } = useEnhancedSystemHealth();

  // حساب صحة النظام المدمج
  const systemHealth = {
    database: health.dbConnected ? 'healthy' : 'error',
    attendance: (stats?.employees.attendanceRate || 0) >= 80 ? 'healthy' : 
                (stats?.employees.attendanceRate || 0) >= 60 ? 'warning' : 'error',
    tasks: health.systemLoad.overdueTasksCount === 0 ? 'healthy' : 
           health.systemLoad.overdueTasksCount <= 5 ? 'warning' : 'error',
    notifications: health.systemLoad.unreadNotifications <= 10 ? 'healthy' : 
                   health.systemLoad.unreadNotifications <= 30 ? 'warning' : 'error',
    performance: health.responseTime <= 1000 ? 'healthy' : 
                 health.responseTime <= 3000 ? 'warning' : 'error'
  };

  const getHealthColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'text-green-600 bg-green-100';
      case 'warning': return 'text-yellow-600 bg-yellow-100';
      case 'error': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getHealthIcon = (status: string) => {
    switch (status) {
      case 'healthy': return CheckCircle;
      case 'warning': return AlertTriangle;
      case 'error': return XCircle;
      default: return Activity;
    }
  };

  const healthItems = [
    {
      name: 'قاعدة البيانات',
      status: systemHealth.database,
      description: health.dbConnected ? `متصلة (${health.responseTime}ms)` : 'خطأ في الاتصال',
      icon: Database
    },
    {
      name: 'حضور الموظفين',
      status: systemHealth.attendance,
      description: `معدل الحضور: ${stats?.employees.attendanceRate || 0}%`,
      icon: Users
    },
    {
      name: 'المهام المتأخرة',
      status: systemHealth.tasks,
      description: `عدد المهام المتأخرة: ${health.systemLoad.overdueTasksCount}`,
      icon: Clock
    },
    {
      name: 'الإشعارات',
      status: systemHealth.notifications,
      description: `غير مقروءة: ${health.systemLoad.unreadNotifications} من ${health.systemLoad.notificationsCount}`,
      icon: Server
    },
    {
      name: 'أداء النظام',
      status: systemHealth.performance,
      description: `نقاط الصحة: ${health.healthScore}%`,
      icon: Activity
    }
  ];

  const overallHealth = Object.values(systemHealth).filter(s => s === 'healthy').length / Object.values(systemHealth).length * 100;

  return (
    <Card className="shadow-lg">
      <CardHeader>
        <CardTitle className="flex items-center justify-between text-xl font-bold text-gray-800">
          <div className="flex items-center gap-2">
            <Activity className="w-6 h-6" />
            صحة النظام
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={checkSystemHealth}
            className="h-8 w-8 p-0"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* المؤشر العام */}
        <div className="text-center space-y-3">
          <div className="text-3xl font-bold text-gray-800">
            {Math.round(overallHealth)}%
          </div>
          <div className="text-sm text-gray-600">
            صحة النظام العامة
          </div>
          <Progress value={overallHealth} className="h-3" />
        </div>

        {/* تفاصيل المكونات */}
        <div className="space-y-3">
          {healthItems.map((item, index) => {
            const Icon = item.icon;
            const StatusIcon = getHealthIcon(item.status);
            
            return (
              <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                <div className="flex items-center gap-3">
                  <Icon className="w-5 h-5 text-gray-600" />
                  <div>
                    <div className="font-semibold text-sm text-gray-900">
                      {item.name}
                    </div>
                    <div className="text-xs text-gray-600">
                      {item.description}
                    </div>
                  </div>
                </div>
                <Badge className={`${getHealthColor(item.status)} border-0`}>
                  <StatusIcon className="w-3 h-3 mr-1" />
                  {item.status === 'healthy' ? 'سليم' :
                   item.status === 'warning' ? 'تحذير' : 'خطأ'}
                </Badge>
              </div>
            );
          })}
        </div>

        {/* آخر فحص وحالة التحميل */}
        <div className="text-center text-xs text-gray-500 border-t pt-3">
          <div>آخر فحص: {health.lastCheck.toLocaleTimeString('ar-SA')}</div>
          {isLoading && <div className="text-blue-600 mt-1">جاري التحديث...</div>}
          {health.criticalIssues.length > 0 && (
            <div className="text-red-600 mt-1 font-medium">يتطلب تدخل فوري!</div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
